import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  acceptServiceOrder,
  addAssignedServiceOrderAttachment,
  completeAssignedServiceOrder,
  getAssignedRelatedServiceOrder,
  getAssignedRelatedServiceOrderAttachment,
  getAssignedRelatedServiceOrderDocument,
  getAssignedServiceOrder,
  getAssignedServiceOrderAttachment,
  getAssignedServiceOrderDocument,
  getServiceOrder,
  getServiceOrderAttachment,
  getServiceOrderDocument,
  removeAssignedServiceOrderAttachment,
  revertAssignedServiceOrder,
  saveAssignedServiceOrderReport,
  saveServiceOrderSettlement,
  type ServiceOrderAttachmentContentDto,
  type ServiceOrderAttachmentRequest,
  type ServiceOrderDocumentContentDto,
  type ServiceOrderDto,
  type ServiceOrderReportRequest,
  type ServiceOrderSettlementRequest,
} from '@/api/service-order.api'
import { getServiceRequest, type ServiceRequestDto } from '@/api/service-request.api'
import { resetStore } from '@/store/actions/reset'
import { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

/**
 * Kierownik czyta każde zlecenie, serwisant — przypisane do siebie. Zlecenie powiązane serwisant czyta przez
 * przypisane zlecenie `relatedTo` (zakres `assigned`).
 */
export type ServiceOrderScope = 'manager' | 'assigned'

type ReadTarget = { id: string; scope: ServiceOrderScope; relatedTo?: string }

interface ServiceOrderDetailsState {
  order: ServiceOrderDto | null
  /** Odrzucone zgłoszenie otwarte z archiwum — nie jest zleceniem. */
  rejectedRequest: ServiceRequestDto | null
  status: Status
  requestId: string | null
  /** Zapis, zakończenie i wycofanie raportu serwisanta. */
  reportStatus: Status
  /** Zapis rozliczenia i akceptacja kierownika. */
  settlementStatus: Status
  /** Załączniki wysyła się równolegle — liczba trwających wysyłek i usunięć. */
  attachmentsPending: number
}

const initialState: ServiceOrderDetailsState = {
  order: null,
  rejectedRequest: null,
  status: 'idle',
  requestId: null,
  reportStatus: 'idle',
  settlementStatus: 'idle',
  attachmentsPending: 0,
}

const withRejection =
  <A, R>(call: (arg: A) => Promise<R>) =>
  async (arg: A, { rejectWithValue }: { rejectWithValue: (value: string) => unknown }) => {
    try {
      return await call(arg)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error)) as never
    }
  }

export const fetchServiceOrderDetails = createAsyncThunk<ServiceOrderDto, ReadTarget>(
  'serviceOrderDetails/fetch',
  withRejection(({ id, scope, relatedTo }) =>
    scope === 'manager'
      ? getServiceOrder(id)
      : relatedTo
      ? getAssignedRelatedServiceOrder(relatedTo, id)
      : getAssignedServiceOrder(id),
  ),
)

export const fetchRejectedRequest = createAsyncThunk<ServiceRequestDto, string>(
  'serviceOrderDetails/fetchRejectedRequest',
  withRejection(getServiceRequest),
)

export const saveServiceOrderReport = createAsyncThunk<
  ServiceOrderDto,
  { id: string; payload: ServiceOrderReportRequest }
>(
  'serviceOrderDetails/saveReport',
  withRejection(({ id, payload }) => saveAssignedServiceOrderReport(id, payload)),
)

export const completeServiceOrderReport = createAsyncThunk<
  ServiceOrderDto,
  { id: string; payload: ServiceOrderReportRequest }
>(
  'serviceOrderDetails/complete',
  withRejection(({ id, payload }) => completeAssignedServiceOrder(id, payload)),
)

export const revertServiceOrderStart = createAsyncThunk<ServiceOrderDto, string>(
  'serviceOrderDetails/revert',
  withRejection(revertAssignedServiceOrder),
)

export const addServiceOrderAttachment = createAsyncThunk<
  ServiceOrderDto,
  { id: string; itemId: string; payload: ServiceOrderAttachmentRequest }
>(
  'serviceOrderDetails/addAttachment',
  withRejection(({ id, itemId, payload }) => addAssignedServiceOrderAttachment(id, itemId, payload)),
)

export const removeServiceOrderAttachment = createAsyncThunk<
  ServiceOrderDto,
  { id: string; itemId: string; attachmentId: string }
>(
  'serviceOrderDetails/removeAttachment',
  withRejection(({ id, itemId, attachmentId }) => removeAssignedServiceOrderAttachment(id, itemId, attachmentId)),
)

export const saveServiceOrderSettlementThunk = createAsyncThunk<
  ServiceOrderDto,
  { id: string; payload: ServiceOrderSettlementRequest }
>(
  'serviceOrderDetails/saveSettlement',
  withRejection(({ id, payload }) => saveServiceOrderSettlement(id, payload)),
)

export const acceptServiceOrderThunk = createAsyncThunk<
  ServiceOrderDto,
  { id: string; payload: ServiceOrderSettlementRequest }
>(
  'serviceOrderDetails/accept',
  withRejection(({ id, payload }) => acceptServiceOrder(id, payload)),
)

/** Treść pliku do podglądu — nie trafia do stanu, bo potrafi ważyć megabajty. */
export const fetchServiceOrderDocumentContent = createAsyncThunk<
  ServiceOrderDocumentContentDto,
  ReadTarget & { documentId: string }
>(
  'serviceOrderDetails/fetchDocument',
  withRejection(({ id, documentId, scope, relatedTo }) =>
    scope === 'manager'
      ? getServiceOrderDocument(id, documentId)
      : relatedTo
      ? getAssignedRelatedServiceOrderDocument(relatedTo, id, documentId)
      : getAssignedServiceOrderDocument(id, documentId),
  ),
)

export const fetchServiceOrderAttachmentContent = createAsyncThunk<
  ServiceOrderAttachmentContentDto,
  ReadTarget & { itemId: string; attachmentId: string }
>(
  'serviceOrderDetails/fetchAttachment',
  withRejection(({ id, itemId, attachmentId, scope, relatedTo }) =>
    scope === 'manager'
      ? getServiceOrderAttachment(id, itemId, attachmentId)
      : relatedTo
      ? getAssignedRelatedServiceOrderAttachment(relatedTo, id, itemId, attachmentId)
      : getAssignedServiceOrderAttachment(id, itemId, attachmentId),
  ),
)

export const fetchServiceOrderForPdf = createAsyncThunk<ServiceOrderDto, string>(
  'serviceOrderDetails/fetchForPdf',
  withRejection(getServiceOrder),
)

const REPORT_WRITES = [saveServiceOrderReport, completeServiceOrderReport, revertServiceOrderStart] as const

const SETTLEMENT_WRITES = [saveServiceOrderSettlementThunk, acceptServiceOrderThunk] as const

const ATTACHMENT_WRITES = [addServiceOrderAttachment, removeServiceOrderAttachment] as const

const applyResponse = (state: ServiceOrderDetailsState, order: ServiceOrderDto) => {
  if (state.order?.id === order.id) state.order = order
}

const serviceOrderDetailsSlice = createSlice({
  name: 'serviceOrderDetails',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceOrderDetails.pending, (state, action) => {
        state.requestId = action.meta.requestId
        state.status = 'loading'
        if (state.order?.id !== action.meta.arg.id) state.order = null
        state.rejectedRequest = null
      })
      // Przejście między zleceniami nie może pokazać szczegółów poprzedniego, które wróciły później.
      .addCase(fetchServiceOrderDetails.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'succeeded'
        state.order = action.payload
      })
      .addCase(fetchServiceOrderDetails.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'failed'
        state.order = null
      })
      .addCase(fetchRejectedRequest.pending, (state, action) => {
        state.requestId = action.meta.requestId
        state.status = 'loading'
        state.order = null
        state.rejectedRequest = null
      })
      .addCase(fetchRejectedRequest.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'succeeded'
        state.rejectedRequest = action.payload
      })
      .addCase(fetchRejectedRequest.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.status = 'failed'
      })
      .addCase(resetStore, () => initialState)

    REPORT_WRITES.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.reportStatus = 'loading'
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.reportStatus = 'succeeded'
          applyResponse(state, action.payload)
        })
        .addCase(thunk.rejected, (state) => {
          state.reportStatus = 'failed'
        })
    })

    SETTLEMENT_WRITES.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.settlementStatus = 'loading'
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.settlementStatus = 'succeeded'
          applyResponse(state, action.payload)
        })
        .addCase(thunk.rejected, (state) => {
          state.settlementStatus = 'failed'
        })
    })

    ATTACHMENT_WRITES.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.attachmentsPending += 1
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.attachmentsPending = Math.max(0, state.attachmentsPending - 1)
          applyResponse(state, action.payload)
        })
        .addCase(thunk.rejected, (state) => {
          state.attachmentsPending = Math.max(0, state.attachmentsPending - 1)
        })
    })
  },
})

export default serviceOrderDetailsSlice.reducer
